import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  useTheme,
  alpha,
} from "@mui/material";
import { toast } from "react-toastify";
import { llmApi } from "../services/rag-service";
import {
  useProjects,
  validateSelectedProjects,
} from "../contexts/ProjectContext";
import { isUserAdmin } from "../utils/auth-helpers";
// Import icons for form elements
import EmbeddingIcon from "@mui/icons-material/BubbleChart";
import MultimodalIcon from "@mui/icons-material/PhotoLibrary";
import STTIcon from "@mui/icons-material/Mic";
import DefaultIcon from "@mui/icons-material/Star";
import FolderIcon from "@mui/icons-material/Folder";
import PublicIcon from "@mui/icons-material/Public";
import { useTranslation } from "react-i18next";

const AUTH_HEADER_BEARER = "bearer";
const AUTH_HEADER_NONE = "NONE";

/**
 * Dialog component for registering a private model
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Function to close the dialog
 * @param {string} props.projectId - Selected project ID
 * @param {Function} props.onSuccess - Function to call after successful registration
 */
const ModelRegistrationForm = ({ open, onClose, projectId, onSuccess }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { selectedProjects, availableProjects } = useProjects();

  // Use utility function to ensure we always have valid arrays with stable references
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () => (Array.isArray(availableProjects) ? availableProjects : []),
    [availableProjects],
  );
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false); // Track if user is admin
  const [showProjectSelector, setShowProjectSelector] = useState(
    !projectId &&
      !safeSelectedProjects.includes("all") &&
      safeSelectedProjects.length > 1,
  );
  const [formData, setFormData] = useState({
    provider: "private",
    model: "",
    temperature: 0.7,
    max_tokens: 0,
    endpoint: "",
    auth_header_type: "bearer",
    is_embedding_model: false,
    is_stt_model: false,
    embedding_dimension: 0,
    name: "",
    is_default: false,
    api_key: "",
    project_id: projectId || "",
    is_multimodal: false,
    is_public: false, // Add is_public property
  });

  // Check if user is admin when dialog opens
  useEffect(() => {
    if (open) {
      setIsAdmin(isUserAdmin());
    }
  }, [open]);

  // Update project ID when prop changes and determine if we need to show project selector
  useEffect(() => {
    // Update the project ID in form data
    setFormData((prev) => ({
      ...prev,
      project_id: projectId || "",
    }));

    // Determine if we need to show the project selector
    // Show if no project ID is provided or multiple projects are selected (not 'all')
    if (
      !projectId &&
      !safeSelectedProjects.includes("all") &&
      safeSelectedProjects.length > 1
    ) {
      setShowProjectSelector(true);
    } else {
      setShowProjectSelector(false);
    }
  }, [projectId, safeSelectedProjects]);

  // Get available projects for the dropdown
  const getAvailableProjectOptions = useCallback(() => {
    if (safeSelectedProjects.includes("all")) {
      // If 'all' is selected, show all available projects
      return safeAvailableProjects;
    } else {
      // Otherwise, filter to only show the selected projects
      return safeAvailableProjects.filter((project) =>
        safeSelectedProjects.includes(project.id),
      );
    }
  }, [safeSelectedProjects, safeAvailableProjects]);

  // Fetch providers when dialog opens
  useEffect(() => {
    if (open) {
      fetchProviders();
    }
  }, [open]);

  // Fetch available providers
  const fetchProviders = async () => {
    setProvidersLoading(true);
    try {
      const response = await llmApi.getProviders();
      setProviders(response);
    } catch (error) {
      console.error("Error fetching providers:", error);
      toast.error(t("modelRegistration.failedToLoadProviders"));
      setProviders([
        {
          name: "private",
          supports_embedding: true,
          supports_generation: true,
        },
      ]); // Fallback
    } finally {
      setProvidersLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If provider changed, we'll check compatibility in the useEffect
  };

  const handleAuthHeaderChange = (e) => {
    const selectedAuthHeader = e.target.value;

    setFormData((prev) => ({
      ...prev,
      auth_header_type:
        selectedAuthHeader === AUTH_HEADER_BEARER ||
        selectedAuthHeader === AUTH_HEADER_NONE
          ? selectedAuthHeader
          : "",
      api_key: selectedAuthHeader === AUTH_HEADER_NONE ? "" : prev.api_key,
    }));
  };

  const getAuthHeaderSelectValue = () => {
    if (formData.auth_header_type === AUTH_HEADER_BEARER) {
      return AUTH_HEADER_BEARER;
    }
    if (formData.auth_header_type === AUTH_HEADER_NONE) {
      return AUTH_HEADER_NONE;
    }

    return "custom";
  };

  const isCustomAuthHeader =
    formData.auth_header_type !== AUTH_HEADER_BEARER &&
    formData.auth_header_type !== AUTH_HEADER_NONE;
  const shouldShowApiKey = formData.auth_header_type !== AUTH_HEADER_NONE;

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;

    // Create the updated form data
    let updatedFormData = {
      ...formData,
      [name]: checked,
    };

    // Ensure only one of is_embedding_model, is_multimodal, is_stt_model can be true at a time
    const exclusives = ["is_embedding_model", "is_multimodal", "is_stt_model"];
    if (checked && exclusives.includes(name)) {
      exclusives.forEach((key) => {
        updatedFormData[key] = key === name;
      });
    }

    setFormData(updatedFormData);

    // If changing embedding model status, reset provider if incompatible
    if (name === "is_embedding_model") {
      const selectedProvider = providers.find(
        (p) => p.name === formData.provider,
      );
      if (selectedProvider) {
        const isCompatible = checked
          ? selectedProvider.supports_embedding
          : selectedProvider.supports_generation;

        if (!isCompatible) {
          // Auto-switch to the first compatible provider
          const compatibleProvider = providers.find((p) =>
            checked ? p.supports_embedding : p.supports_generation,
          );

          if (compatibleProvider) {
            setFormData((prev) => ({
              ...prev,
              provider: compatibleProvider.name,
              [name]: checked,
              is_multimodal:
                name === "is_embedding_model" && checked
                  ? false
                  : prev.is_multimodal,
              is_stt_model:
                name === "is_embedding_model" && checked
                  ? false
                  : prev.is_stt_model,
            }));
            toast.info(
              t("modelRegistration.providerChanged", {
                provider: compatibleProvider.name,
              }),
            );
          }
        }
      }
    }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value === "" ? "" : Number(value),
    });
  };

  // Check if selected provider is compatible with model type
  // eslint-disable-next-line no-unused-vars
  const isProviderCompatible = (provider) => {
    const selectedProvider = providers.find((p) => p.name === provider);
    if (!selectedProvider) return true; // Default to true if provider not found

    // Check compatibility with the selected model type
    if (formData.is_embedding_model) {
      return selectedProvider.supports_embedding;
    } else if (formData.is_multimodal) {
      return selectedProvider.supports_multimodal;
    } else {
      return selectedProvider.supports_generation;
    }
  };

  // Check if the current provider supports specific capabilities
  const getProviderCapabilities = useCallback(() => {
    const selectedProvider = providers.find(
      (p) => p.name === formData.provider,
    );
    if (!selectedProvider) {
      // If provider not found, default to allowing all capabilities
      return {
        supportsEmbedding: true,
        supportsGeneration: true,
        supportsMultimodal: true,
        supportsSTT: true,
      };
    }

    return {
      supportsEmbedding: selectedProvider.supports_embedding,
      supportsGeneration: selectedProvider.supports_generation,
      supportsMultimodal: selectedProvider.supports_multimodal,
      supportsSTT: selectedProvider.supports_stt,
    };
  }, [providers, formData.provider]);

  // Effect to update form when provider changes
  useEffect(() => {
    // Only run this effect if providers have been loaded
    if (providers.length === 0 || providersLoading) return;

    const capabilities = getProviderCapabilities();

    // If current provider doesn't support the selected model type, reset to compatible options
    if (formData.is_embedding_model && !capabilities.supportsEmbedding) {
      setFormData((prev) => ({
        ...prev,
        is_embedding_model: false,
      }));
      toast.info(
        t("modelRegistration.providerDoesNotSupportEmbedding", {
          provider: formData.provider,
        }),
      );
    }

    if (formData.is_multimodal && !capabilities.supportsMultimodal) {
      setFormData((prev) => ({
        ...prev,
        is_multimodal: false,
      }));
      toast.info(
        t("modelRegistration.providerDoesNotSupportMultimodal", {
          provider: formData.provider,
        }),
      );
    }

    if (formData.is_stt_model && !capabilities.supportsSTT) {
      setFormData((prev) => ({
        ...prev,
        is_stt_model: false,
      }));
      toast.info(
        t("modelRegistration.providerDoesNotSupportStt", {
          provider: formData.provider,
        }),
      );
    }
  }, [
    formData.provider,
    providers,
    providersLoading,
    formData.is_embedding_model,
    formData.is_multimodal,
    formData.is_stt_model,
    getProviderCapabilities,
  ]);

  const validateForm = () => {
    if (!formData.name) return t("modelRegistration.nameRequired");
    if (!formData.model) return t("modelRegistration.modelIdentifierRequired");
    if (!formData.endpoint) return t("modelRegistration.endpointRequired");
    if (shouldShowApiKey && !formData.api_key) {
      return t("aiCatalog.apiKeyRequired");
    }
    if (isCustomAuthHeader && !formData.auth_header_type.trim()) {
      return t("modelRegistration.customHeaderRequired");
    }

    // Enhanced project validation
    if (!formData.project_id) {
      if (showProjectSelector) {
        return t("modelRegistration.selectProjectFromDropdown");
      } else {
        return t("modelRegistration.projectRequired");
      }
    }

    const capabilities = getProviderCapabilities();

    // Check if selected provider is compatible with the model type
    if (formData.is_embedding_model && !capabilities.supportsEmbedding) {
      return t("modelRegistration.providerDoesNotSupportEmbedding", {
        provider: formData.provider,
      });
    }

    if (formData.is_multimodal && !capabilities.supportsMultimodal) {
      return t("modelRegistration.providerDoesNotSupportMultimodal", {
        provider: formData.provider,
      });
    }

    if (formData.is_stt_model && !capabilities.supportsSTT) {
      return t("modelRegistration.providerDoesNotSupportStt", {
        provider: formData.provider,
      });
    }

    if (
      !formData.is_embedding_model &&
      !formData.is_multimodal &&
      !formData.is_stt_model &&
      !capabilities.supportsGeneration
    ) {
      return t("modelRegistration.generationProvidersOnly");
    }

    // Validate embedding model settings
    if (formData.is_embedding_model) {
      if (formData.is_multimodal) {
        return t("modelRegistration.cannotBeEmbeddingAndMultimodal");
      }
      if (formData.is_stt_model) {
        return t("modelRegistration.cannotBeEmbeddingAndStt");
      }
      if (!formData.embedding_dimension || formData.embedding_dimension <= 0) {
        return t("modelRegistration.embeddingDimensionPositive");
      }
    }
    // Validate non-embedding model settings
    else {
      if (formData.temperature < 0 || formData.temperature > 1) {
        return t("modelRegistration.temperatureRange");
      }
    }

    return "";
  };

  // Filter providers based on model type
  const getFilteredProviders = () => {
    if (providers.length === 0) return [];

    if (formData.is_embedding_model) {
      return providers.filter((provider) => provider.supports_embedding);
    } else if (formData.is_multimodal) {
      return providers.filter((provider) => provider.supports_multimodal);
    } else if (formData.is_stt_model) {
      return providers.filter((provider) => provider.supports_stt);
    } else {
      return providers.filter((provider) => provider.supports_generation);
    }
  };

  const handleSubmit = async () => {
    // Validate form
    const error = validateForm();
    if (error) {
      setFormError(error);
      // Smooth scroll to the top of the dialog content
      const dialogContent = document.querySelector(".MuiDialogContent-root");
      if (dialogContent) {
        dialogContent.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      return;
    }

    setFormSubmitting(true);
    setFormError("");

    try {
      // Format the data for the API
      const requestData = {
        provider: formData.provider,
        model: formData.model,
        temperature: formData.temperature,
        max_tokens: formData.max_tokens,
        extra_config: {},
        endpoint: formData.endpoint,
        auth_header_type: formData.auth_header_type,
        is_embedding_model: formData.is_embedding_model,
        embedding_dimension: formData.is_embedding_model
          ? formData.embedding_dimension
          : null,
        name: formData.name,
        is_default: formData.is_default,
        project_id: formData.project_id,
        is_multimodal: formData.is_multimodal,
        is_stt_model: formData.is_stt_model,
      };

      // Add is_public property if user is admin
      if (isAdmin) {
        requestData.is_public = formData.is_public;
      }

      // Add API key to extra_config if provided
      if (shouldShowApiKey && formData.api_key) {
        requestData.extra_config = {
          api_key: formData.api_key,
        };
      }

      // Submit to the API
      // Use admin endpoint if the user is an admin, otherwise use user endpoint
      if (isAdmin) {
        await llmApi.createAdminLLMConfig(requestData);
      } else {
        await llmApi.createUserLLMConfig(requestData);
      }

      // Show success toast
      toast.success(t("modelRegistration.registerSuccess"));

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close dialog
      onClose();
    } catch (error) {
      console.error("Error registering model:", error);
      setFormError(error.message || t("modelRegistration.registerFailed"));
      // Smooth scroll to the top of the dialog content
      const dialogContent = document.querySelector(".MuiDialogContent-root");
      if (dialogContent) {
        dialogContent.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      toast.error(t("modelRegistration.registerFailed"));
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!formSubmitting ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: formError ? "2px solid" : "1px solid",
          borderColor: formError ? "error.main" : "divider",
          pb: formError ? 2 : 1,
        }}
      >
        {isAdmin
          ? t("modelRegistration.adminTitle")
          : t("modelRegistration.title")}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 3,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: (theme) =>
              theme.palette.mode === "light"
                ? "rgba(0, 0, 0, 0.2)"
                : "rgba(255, 255, 255, 0.1)",
            borderRadius: "4px",
            "&:hover": {
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "rgba(0, 0, 0, 0.3)"
                  : "rgba(255, 255, 255, 0.15)",
            },
          },
        }}
      >
        {formError && (
          <Alert
            severity="error"
            sx={{
              mb: 3, // More space after error
              animation: "shake 0.5s cubic-bezier(.36,.07,.19,.97) both",
              "@keyframes shake": {
                "10%, 90%": {
                  transform: "translate3d(-1px, 0, 0)",
                },
                "20%, 80%": {
                  transform: "translate3d(2px, 0, 0)",
                },
                "30%, 50%, 70%": {
                  transform: "translate3d(-4px, 0, 0)",
                },
                "40%, 60%": {
                  transform: "translate3d(4px, 0, 0)",
                },
              },
            }}
          >
            {formError}
          </Alert>
        )}

        {/* Project Selection - Show if no project ID is passed or multiple projects selected */}
        {(showProjectSelector ||
          (!projectId && formData.project_id === "")) && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: (theme) =>
                theme.palette.mode === "light"
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.primary.main, 0.15),
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {t("modelRegistration.projectSelection")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <FormControl fullWidth required error={!formData.project_id}>
              <InputLabel id="project-select-label">
                {t("modelRegistration.selectProjectLabel")}
              </InputLabel>
              <Select
                labelId="project-select-label"
                id="project-select"
                name="project_id"
                value={formData.project_id}
                onChange={handleInputChange}
                label={t("modelRegistration.selectProjectLabel")}
                startAdornment={
                  <InputAdornment position="start">
                    <FolderIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                {getAvailableProjectOptions().map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {!formData.project_id
                  ? t("modelRegistration.projectSelectionRequired")
                  : t("modelRegistration.projectSelectionHelper")}
              </FormHelperText>
            </FormControl>
          </Box>
        )}

        {/* Top Form Section */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            borderRadius: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "background.paper"
                : "background.paper",
            boxShadow: (theme) =>
              theme.palette.mode === "light"
                ? "rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
                : "rgba(0, 0, 0, 0.2) 0px 1px 2px 0px",
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            {t("modelRegistration.basicInformation")}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                name="name"
                label={t("modelRegistration.modelName")}
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                helperText={t("modelRegistration.modelNameHelper")}
                sx={{ mb: 2 }}
              />

              <TextField
                name="model"
                label={t("modelRegistration.modelIdentifier")}
                value={formData.model}
                onChange={handleInputChange}
                fullWidth
                required
                helperText={t("modelRegistration.modelIdentifierHelper")}
                sx={{ mb: 2 }}
              />

              <TextField
                className="url-textfield"
                name="endpoint"
                label={t("modelRegistration.endpointUrl")}
                value={formData.endpoint}
                onChange={handleInputChange}
                fullWidth
                required
                helperText={t("modelRegistration.endpointUrlHelper")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="auth-header-type-select-label">
                  {t("modelRegistration.authHeaderType")}
                </InputLabel>
                <Select
                  labelId="auth-header-type-select-label"
                  id="auth-header-type-select"
                  name="auth_header_type"
                  value={getAuthHeaderSelectValue()}
                  label={t("modelRegistration.authHeaderType")}
                  onChange={handleAuthHeaderChange}
                >
                  <MenuItem value={AUTH_HEADER_BEARER}>Bearer</MenuItem>
                  <MenuItem value={AUTH_HEADER_NONE}>None</MenuItem>
                  <MenuItem value="custom">{t("common.custom")}</MenuItem>
                </Select>
                <FormHelperText>
                  {t("modelRegistration.authHeaderTypeHelper")}
                </FormHelperText>
              </FormControl>

              {isCustomAuthHeader && (
                <TextField
                  name="auth_header_type"
                  label={t("apiKeyDialog.customHeader")}
                  value={formData.auth_header_type}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  helperText={t("apiKeyDialog.customHeaderPlaceholder")}
                  sx={{ mb: 2 }}
                />
              )}

              {shouldShowApiKey && (
                <TextField
                  name="api_key"
                  label={t("modelRegistration.apiKey")}
                  value={formData.api_key}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  type="password"
                  helperText={t("modelRegistration.apiKeyHelper")}
                  sx={{ mb: 2 }}
                />
              )}

              <FormControl fullWidth>
                <InputLabel id="provider-select-label">
                  {t("modelRegistration.provider")}
                </InputLabel>
                <Select
                  labelId="provider-select-label"
                  id="provider-select"
                  name="provider"
                  value={providersLoading ? "" : formData.provider}
                  onChange={handleInputChange}
                  disabled={providersLoading}
                  label={t("modelRegistration.provider")}
                  startAdornment={
                    providersLoading ? (
                      <InputAdornment position="start">
                        <CircularProgress size={20} color="inherit" />
                      </InputAdornment>
                    ) : null
                  }
                >
                  {providersLoading ? (
                    <MenuItem disabled value="">
                      <em>{t("modelRegistration.loadingProviders")}</em>
                    </MenuItem>
                  ) : getFilteredProviders().length > 0 ? (
                    getFilteredProviders().map((provider) => (
                      <MenuItem key={provider.id} value={provider.name}>
                        {provider.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      <em>{t("modelRegistration.noCompatibleProviders")}</em>
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>
                  {providersLoading
                    ? t("modelRegistration.loadingAvailableProviders")
                    : formData.is_embedding_model
                      ? t("modelRegistration.embeddingProvidersOnly")
                      : formData.is_multimodal
                        ? t("modelRegistration.multimodalProvidersOnly")
                        : formData.is_stt_model
                          ? t("modelRegistration.sttProvidersOnly")
                          : t("modelRegistration.generationProvidersOnly")}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Model Type Section */}
        <Box
          sx={{
            mt: 4,
            p: 2,
            borderRadius: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "background.paper"
                : "background.paper",
            boxShadow: (theme) =>
              theme.palette.mode === "light"
                ? "rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
                : "rgba(0, 0, 0, 0.2) 0px 1px 2px 0px",
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            {t("modelRegistration.modelTypeConfiguration")}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Two-column layout after divider - Left: switches, Right: conditional inputs */}
          <Grid container spacing={3}>
            {/* Left column - Toggle switches */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  height: "100%",
                  p: 2,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "rgba(0, 0, 0, 0.04)"
                      : "rgba(255, 255, 255, 0.05)",
                  boxShadow: (theme) =>
                    theme.palette.mode === "light"
                      ? "inset 0 1px 2px rgba(0,0,0,0.06)"
                      : "inset 0 1px 2px rgba(255,255,255,0.03)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="medium"
                  gutterBottom
                >
                  {t("modelRegistration.modelCapabilities")}
                </Typography>

                {/* Embedding Model Switch */}
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_embedding_model}
                        onChange={handleSwitchChange}
                        name="is_embedding_model"
                        disabled={
                          providersLoading ||
                          formData.is_multimodal ||
                          formData.is_stt_model ||
                          !getProviderCapabilities().supportsEmbedding
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <EmbeddingIcon
                          sx={{
                            marginInlineEnd: 1,
                            color: formData.is_embedding_model
                              ? "primary.main"
                              : "text.secondary",
                          }}
                        />
                        <Typography>
                          {t("modelRegistration.embeddingModel")}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormHelperText>
                    {!getProviderCapabilities().supportsEmbedding
                      ? t("modelRegistration.providerDoesNotSupportEmbedding", {
                          provider: formData.provider,
                        })
                      : formData.is_multimodal
                        ? t(
                            "modelRegistration.disabledMultimodalCannotBeEmbedding",
                          )
                        : formData.is_stt_model
                          ? t("modelRegistration.disabledSttCannotBeEmbedding")
                          : formData.is_embedding_model
                            ? t("modelRegistration.createsVectorEmbeddings")
                            : t(
                                "modelRegistration.generateTextWithoutEmbeddings",
                              )}
                  </FormHelperText>
                </FormControl>

                {/* Multimodal Model Switch */}
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_multimodal}
                        onChange={handleSwitchChange}
                        name="is_multimodal"
                        disabled={
                          providersLoading ||
                          formData.is_embedding_model ||
                          formData.is_stt_model ||
                          !getProviderCapabilities().supportsMultimodal
                        }
                        color="secondary"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <MultimodalIcon
                          sx={{
                            marginInlineEnd: 1,
                            color: formData.is_multimodal
                              ? "secondary.main"
                              : "text.secondary",
                          }}
                        />
                        <Typography>
                          {t("modelRegistration.multimodalModel")}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormHelperText>
                    {!getProviderCapabilities().supportsMultimodal
                      ? t(
                          "modelRegistration.providerDoesNotSupportMultimodal",
                          {
                            provider: formData.provider,
                          },
                        )
                      : formData.is_embedding_model
                        ? t(
                            "modelRegistration.disabledEmbeddingCannotBeMultimodal",
                          )
                        : formData.is_stt_model
                          ? t("modelRegistration.disabledSttCannotBeMultimodal")
                          : formData.is_multimodal
                            ? t(
                                "modelRegistration.supportsTextImagesOtherModalities",
                              )
                            : t("modelRegistration.textOnlyModel")}
                  </FormHelperText>
                </FormControl>

                {/* Speech-to-Text Model Switch */}
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_stt_model}
                        onChange={handleSwitchChange}
                        name="is_stt_model"
                        disabled={
                          providersLoading ||
                          formData.is_embedding_model ||
                          formData.is_multimodal ||
                          !getProviderCapabilities().supportsSTT
                        }
                        color="success"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <STTIcon
                          sx={{
                            marginInlineEnd: 1,
                            color: formData.is_stt_model
                              ? "success.main"
                              : "text.secondary",
                          }}
                        />
                        <Typography>
                          {t("modelRegistration.speechToTextModel")}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormHelperText>
                    {!getProviderCapabilities().supportsSTT
                      ? t("modelRegistration.providerDoesNotSupportStt", {
                          provider: formData.provider,
                        })
                      : formData.is_embedding_model
                        ? t("modelRegistration.disabledEmbeddingCannotBeStt")
                        : formData.is_multimodal
                          ? t("modelRegistration.disabledMultimodalCannotBeStt")
                          : formData.is_stt_model
                            ? t("modelRegistration.convertsSpeechToText")
                            : t("modelRegistration.notSpeechToTextModel")}
                  </FormHelperText>
                </FormControl>

                {/* Default Model Switch */}
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_default}
                        onChange={handleSwitchChange}
                        name="is_default"
                        disabled={providersLoading}
                        color="warning"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <DefaultIcon
                          sx={{
                            marginInlineEnd: 1,
                            color: formData.is_default
                              ? "warning.main"
                              : "text.secondary",
                          }}
                        />
                        <Typography>
                          {t("modelRegistration.defaultModel")}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormHelperText>
                    {formData.is_default
                      ? t("modelRegistration.usedAsDefault")
                      : t("modelRegistration.notDefault")}
                  </FormHelperText>
                </FormControl>

                {/* Public Model Switch - Only shown for admins */}
                {isAdmin && (
                  <FormControl component="fieldset">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_public}
                          onChange={handleSwitchChange}
                          name="is_public"
                          disabled={providersLoading}
                          color="info"
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PublicIcon
                            sx={{
                              marginInlineEnd: 1,
                              color: formData.is_public
                                ? "info.main"
                                : "text.secondary",
                            }}
                          />
                          <Typography>
                            {t("modelRegistration.publicModel")}
                          </Typography>
                        </Box>
                      }
                    />
                    <FormHelperText>
                      {formData.is_public
                        ? t("modelRegistration.availableToAllUsers")
                        : t("modelRegistration.explicitAccessOnly")}
                    </FormHelperText>
                  </FormControl>
                )}
              </Box>
            </Grid>

            {/* Right column - Conditional inputs based on model type */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: 1,
                  bgcolor: (theme) => {
                    if (formData.is_embedding_model) {
                      return theme.palette.mode === "light"
                        ? "rgba(16, 185, 129, 0.08)"
                        : "rgba(16, 185, 129, 0.08)"; // Subtle green tint for both modes
                    } else if (formData.is_multimodal) {
                      return theme.palette.mode === "light"
                        ? "rgba(110, 231, 183, 0.08)"
                        : "rgba(110, 231, 183, 0.08)"; // Subtle teal tint for both modes
                    } else if (formData.is_stt_model) {
                      return theme.palette.mode === "light"
                        ? "rgba(76, 175, 80, 0.08)"
                        : "rgba(76, 175, 80, 0.08)"; // Subtle green tint for STT models
                    } else {
                      return theme.palette.mode === "light"
                        ? "background.default"
                        : "background.paper";
                    }
                  },
                  boxShadow: (theme) =>
                    theme.palette.mode === "light"
                      ? "inset 0 1px 2px rgba(0,0,0,0.03)"
                      : "inset 0 1px 2px rgba(255,255,255,0.03)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="medium"
                  gutterBottom
                >
                  {formData.is_embedding_model
                    ? t("modelRegistration.embeddingConfiguration")
                    : formData.is_stt_model
                      ? t("modelRegistration.speechToTextConfiguration")
                      : t("modelRegistration.generationConfiguration")}
                </Typography>

                {/* Conditional fields based on model type */}
                {formData.is_embedding_model ? (
                  <TextField
                    name="embedding_dimension"
                    label={t("modelRegistration.embeddingDimension")}
                    type="number"
                    value={formData.embedding_dimension}
                    onChange={handleNumberChange}
                    fullWidth
                    margin="normal"
                    required
                    inputProps={{ min: 1 }}
                    helperText={t("modelRegistration.embeddingDimensionHelper")}
                    disabled={providersLoading}
                  />
                ) : formData.is_stt_model ? (
                  <Alert
                    severity="info"
                    variant="outlined"
                    sx={{
                      mt: 2,
                      borderColor:
                        theme.palette.mode === "light"
                          ? "success.light"
                          : "success.dark",
                      bgcolor:
                        theme.palette.mode === "light"
                          ? "rgba(76, 175, 80, 0.1)"
                          : "rgba(76, 175, 80, 0.05)",
                    }}
                  >
                    This model will be configured to convert speech input to
                    text output. The model will process audio data and return
                    transcribed text.
                  </Alert>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="temperature"
                          label={t("modelRegistration.temperature")}
                          type="number"
                          value={formData.temperature}
                          onChange={handleNumberChange}
                          fullWidth
                          margin="normal"
                          inputProps={{ min: 0, max: 1, step: 0.1 }}
                          helperText={t("modelRegistration.temperatureHelper")}
                          disabled={providersLoading}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          name="max_tokens"
                          label={t("modelRegistration.maxTokens")}
                          type="number"
                          value={formData.max_tokens}
                          onChange={handleNumberChange}
                          fullWidth
                          margin="normal"
                          inputProps={{ min: 0 }}
                          helperText={t("modelRegistration.maxTokensHelper")}
                          disabled={providersLoading}
                        />
                      </Grid>
                    </Grid>

                    {/* Additional info for multimodal models */}
                    {formData.is_multimodal && (
                      <Alert
                        severity="info"
                        variant="outlined"
                        sx={{
                          mt: 2,
                          borderColor:
                            theme.palette.mode === "light"
                              ? "info.light"
                              : "info.dark",
                          bgcolor:
                            theme.palette.mode === "light"
                              ? "rgba(144, 202, 249, 0.1)"
                              : "rgba(144, 202, 249, 0.05)",
                        }}
                      >
                        This model will be configured to accept multiple input
                        types (text, images, etc.) depending on the provider's
                        capabilities.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={onClose}
          disabled={formSubmitting || providersLoading}
          color="secondary"
          variant="text"
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={formSubmitting || providersLoading}
          startIcon={formSubmitting ? <CircularProgress size={20} /> : null}
          sx={{ px: 3 }}
        >
          {formSubmitting
            ? t("modelRegistration.registering")
            : t("modelRegistration.registerModel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModelRegistrationForm;
